/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { clearAllPersistedCarts } from "@farmlands/commerce";
import { AnyAction, combineReducers } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";

import AuthSlice, { authPersistConfig } from "../features/azure-b2c/storage";
import CartSlice, { cartPersistConfig } from "../features/cart/storage/cart.slice";
import CostPriceSlice, { costPricePersistConfig } from "../features/cost-price/costprice.slice";
import DeclarationsSlice, {
  declarationsPersistConfig,
} from "../features/declarations/declarations.slice";
import OrderSlice, { ordersPersistConfig } from "../features/orders/storage/orders.slice";
import { stocksPersistConfig, StocksSlice } from "../features/products/storage/stocks.slice";
import QuoteSlice from "../features/quotes/storage/quotes.slice";
import SalesSlice, { salesPersistConfig } from "../features/sales-agreements/storage/sales.slice";
import SeedCartSlice from "../features/seed-orders/storage/seedCart.slice";
import SeedStoresSlice from "../features/seed-orders/storage/seedStores.slice";
import StoresSlice, { storesPersistConfig } from "../features/stores/storage/stores.slice";
import RemoteConfigSlice, {
  remoteConfigState,
} from "../services/firebase/remote-config/remoteConfig.slice";
import { storageDb } from "../services/localforage";

export function clearLocalStorageExceptWhitelist() {
  const whitelist = ["mcid"]; // Add more keys here if needed

  // Get all keys from localStorage
  const keys = Object.keys(localStorage);

  // Iterate over all keys
  keys.forEach((key) => {
    // Remove the key if it's not in the whitelist
    if (!whitelist.includes(key)) {
      localStorage.removeItem(key);
    }
  });
}

const reducers = {
  auth: persistReducer(authPersistConfig, AuthSlice.reducer),
  cart: persistReducer(cartPersistConfig, CartSlice.reducer),
  costPrice: persistReducer(costPricePersistConfig, CostPriceSlice.reducer),
  declarations: persistReducer(declarationsPersistConfig, DeclarationsSlice.reducer),
  orders: persistReducer(ordersPersistConfig, OrderSlice.reducer),
  quotes: QuoteSlice.reducer,
  remoteConfig: persistReducer(remoteConfigState, RemoteConfigSlice.reducer),
  sales: persistReducer(salesPersistConfig, SalesSlice.reducer),
  seedCart: SeedCartSlice.reducer,
  seedStores: SeedStoresSlice.reducer,
  stocks: persistReducer(stocksPersistConfig, StocksSlice.reducer),
  stores: persistReducer(storesPersistConfig, StoresSlice.reducer),
};
const appReducer = combineReducers(reducers);

type AppState = ReturnType<typeof appReducer>;

const rootReducer = (state: AppState | undefined, action: AnyAction) => {
  if (action.type === "LOGOUT_REQUEST") {
    storageDb.clear();
    clearAllPersistedCarts();
    clearLocalStorageExceptWhitelist();
    sessionStorage.clear();
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

export type RootState = ReturnType<typeof rootReducer>;

export default rootReducer;
